/*
* Project: LegalPlace assistant
* File: src/components/misc/masked_input.js
* Author: Nasreddine DAIFALLAH
* Version: 1.0
*/

import React from 'react';
import PropTypes from 'prop-types';
import InputMask from '../utils/input_mask';

const KEYCODE_Z = 90;
const KEYCODE_Y = 89;

function isUndo(e) {
  return (e.ctrlKey || e.metaKey) && e.keyCode === (e.shiftKey ? KEYCODE_Y : KEYCODE_Z);
}

function isRedo(e) {
  return (e.ctrlKey || e.metaKey) && e.keyCode === (e.shiftKey ? KEYCODE_Z : KEYCODE_Y);
}

function getSelection(el) {
  let start; let end; let rangeEl; let
    clone;

  if (el.selectionStart !== undefined) {
    start = el.selectionStart;
    end = el.selectionEnd;
  } else {
    try {
      el.focus();
      rangeEl = el.createTextRange();
      clone = rangeEl.duplicate();

      rangeEl.moveToBookmark(document.selection.createRange().getBookmark());
      clone.setEndPoint('EndToStart', rangeEl);

      start = clone.text.length;
      end = start + rangeEl.text.length;
    } catch (e) { /* not focused or not visible */ }
  }

  return { start, end };
}

function setSelection(el, selection) {
  let rangeEl;

  try {
    if (el.selectionStart !== undefined) {
      el.focus();
      el.setSelectionRange(selection.start, selection.end);
    } else {
      el.focus();
      rangeEl = el.createTextRange();
      rangeEl.collapse(true);
      rangeEl.moveStart('character', selection.start);
      rangeEl.moveEnd('character', selection.end - selection.start);
      rangeEl.select();
    }
  } catch (e) { /* not focused or not visible */ }
}

class MaskedInput extends React.Component {
  static keyPressPropName() {
    if (typeof navigator !== 'undefined') {
      return navigator.userAgent.match(/Android/i)
        ? 'onBeforeInput'
        : 'onKeyPress';
    }
    return 'onKeyPress';
  }

  constructor(props) {
    super(props);

    this.onChange = this.onChange.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onPaste = this.onPaste.bind(this);
    this.onKeyPress = this.onKeyPress.bind(this);
    this.updateInputSelection = this.updateInputSelection.bind(this);
  }

  componentWillMount() {
    const options = {
      pattern: this.props.mask,
      value: this.props.value,
      formatCharacters: this.props.formatCharacters,
    };
    if (this.props.placeholderChar) {
      options.placeholderChar = this.props.placeholderChar;
    }
    this.mask = new InputMask(options);
  }

  componentDidMount(){
    if(document.getElementById('numcard1') && document.getElementById('numcard2')){
      document.getElementById('numcard1').setAttribute('autoFocus', true);
      document.getElementById('numcard2').setAttribute('autoFocus', true);
    }
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.mask !== nextProps.mask && this.props.value !== nextProps.mask) {
      // if we get a new value and a new mask at the same time
      // check if the mask.value is still the initial value
      // - if so use the nextProps value
      // - otherwise the `this.mask` has a value for us (most likely from paste action)
      if (this.mask.getValue() === this.mask.emptyValue) {
        this.mask.setPattern(nextProps.mask, { value: nextProps.value });
      } else {
        this.mask.setPattern(nextProps.mask, { value: this.mask.getRawValue() });
      }
    } else if (this.props.mask !== nextProps.mask) {
      this.mask.setPattern(nextProps.mask, { value: this.mask.getRawValue() });
    } else if (this.props.value !== nextProps.value) {
      this.mask.setValue(nextProps.value);
    }
  }

  componentWillUpdate(nextProps) {
    if (nextProps.mask !== this.props.mask) {
      this.updatePattern(nextProps);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.mask !== this.props.mask && this.mask.selection.start) {
      this.updateInputSelection();
    }
    if(document.getElementById('numcard1') && document.getElementById('numcard2')){
      document.getElementById('numcard1').setAttribute('autoFocus', true);
      document.getElementById('numcard2').setAttribute('autoFocus', true);
    }
  }

  onChange(e) {
    // console.log('onChange', JSON.stringify(getSelection(this.input)), e.target.value)

    const maskValue = this.mask.getValue();
    const incomingValue = e.target.value;
    if (incomingValue !== maskValue) { // only modify mask if form contents actually changed
      this.updateMaskSelection();
      this.mask.setValue(incomingValue); // write the whole updated value into the mask
      e.target.value = this.getDisplayValue(); // update the form with pattern applied to the value
      this.updateInputSelection();
    }

    if (this.props.onChange) {
      this.props.onChange(e);
    }
  }

  onKeyDown(e) {
    // console.log('onKeyDown', JSON.stringify(getSelection(this.input)), e.key, e.target.value)

    if (isUndo(e)) {
      e.preventDefault();
      if (this.mask.undo()) {
        e.target.value = this.getDisplayValue();
        this.updateInputSelection();
        if (this.props.onChange) {
          this.props.onChange(e);
        }
      }
      return;
    }
    if (isRedo(e)) {
      e.preventDefault();
      if (this.mask.redo()) {
        e.target.value = this.getDisplayValue();
        this.updateInputSelection();
        if (this.props.onChange) {
          this.props.onChange(e);
        }
      }
      return;
    }

    if (e.key === 'Backspace') {
      e.preventDefault();
      this.updateMaskSelection();
      if (this.mask.backspace()) {
        const value = this.getDisplayValue();
        e.target.value = value;
        if (value) {
          this.updateInputSelection();
        }
        if (this.props.onChange) {
          this.props.onChange(e);
        }
      }
    }
  }

  onKeyPress(e) {
    // console.log('onKeyPress', JSON.stringify(getSelection(this.input)), e.key, e.target.value)

    // Ignore modified key presses
    // Ignore enter key to allow form submission
    if (e.metaKey || e.altKey || e.ctrlKey || e.key === 'Enter') { return; }

    e.preventDefault();
    this.updateMaskSelection();
    if (this.mask.input((e.key || e.data))) {
      e.target.value = this.mask.getValue();
      this.updateInputSelection();
      if (this.props.onChange) {
        this.props.onChange(e);
      }
    }
  }

  onPaste(e) {
    e.preventDefault();
    this.updateMaskSelection();
    // getData value needed for IE also works in FF & Chrome
    if (this.mask.paste(e.clipboardData.getData('Text'))) {
      e.target.value = this.mask.getValue();
      // Timeout needed for IE
      setTimeout(this.updateInputSelection.bind(this), 0);
      if (this.props.onChange) {
        this.props.onChange(e);
      }
    }
  }

  getEventHandlers() {
    return {
      onChange: this.onChange,
      onKeyDown: this.onKeyDown,
      onPaste: this.onPaste,
      [MaskedInput.keyPressPropName()]: this.onKeyPress,
    };
  }

  getDisplayValue() {
    const value = this.mask.getValue();
    return value === this.mask.emptyValue ? '' : value;
  }

  updatePattern(props) {
    this.mask.setPattern(props.mask, {
      value: this.mask.getRawValue(),
      selection: getSelection(this.input),
    });
  }

  updateMaskSelection() {
    this.mask.selection = getSelection(this.input);
  }

  updateInputSelection() {
    setSelection(this.input, this.mask.selection);
  }

  focus() {
    this.input.focus();
  }

  blur() {
    this.input.blur();
  }

  render() {
    const ref = (r) => { this.input = r; };
    const maxLength = this.mask.pattern.length;
    let placeholder = this.getDisplayValue();
    const eventHandlers = this.getEventHandlers();
    const { size = maxLength } = this.props;

    const { ...cleanedProps } = this.props;
    if (this.props.placeholder !== '') ({ placeholder } = this.props);
    const inputProps = {
      ...cleanedProps, ...eventHandlers, ref, maxLength, size, placeholder,
    };
    return <input {...inputProps} />;
  }
}

MaskedInput.propTypes = {
  mask: PropTypes.string.isRequired,
  formatCharacters: PropTypes.object,
  placeholderChar: PropTypes.string,
  value: PropTypes.string,
};

MaskedInput.defaultProps = {
  value: '',
  formatCharacters: {},
  placeholderChar: '',
};

export default MaskedInput;



// WEBPACK FOOTER //
// ./src/components/misc/masked_input.js